"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  Mail,
  MapPin,
  Linkedin,
  Github,
  Send,
  ArrowUpRight,
  CheckCircle,
  Phone,
} from "lucide-react";
import { personalInfo } from "../data/mock";
import Layout from "../components/layout/Layout";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  // 🔥 REAL SUBMIT (RESEND API)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (data.success) {
        setSubmitted(true);
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        alert("Something went wrong. Try again.");
      }
    } catch (err) {
      console.error(err);
      alert("Error sending message");
    }

    setLoading(false);
  };

  // 🔥 CONTACT METHODS
  const contactMethods = [
    {
      icon: Mail,
      label: "Email",
      value: personalInfo.email,
      href: `mailto:${personalInfo.email}`,
    },
    {
      icon: Phone,
      label: "WhatsApp",
      value: "Chat on WhatsApp",
      href: "https://wa.me/919521347419", // 🔴 replace
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      value: "Connect on LinkedIn",
      href: personalInfo.linkedin,
    },
    {
      icon: Github,
      label: "GitHub",
      value: "View Projects",
      href: personalInfo.github,
    },
    {
      icon: MapPin,
      label: "Location",
      value: personalInfo.location,
      href: null,
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <Layout>
      {/* SEO */}
      <Helmet>
        <title>Contact | Gauransh Jaroli</title>
        <meta
          name="description"
          content="Contact Gauransh Jaroli for website development, SaaS, and business systems."
        />
      </Helmet>

      {/* HERO */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-950" />

        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-emerald-400/10 blur-3xl rounded-full" />
        <div className="absolute -top-32 right-0 w-[420px] h-[420px] bg-blue-500/10 blur-3xl rounded-full" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-10 max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Get in{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-blue-500 bg-clip-text text-transparent">
                Touch
              </span>
            </h1>

            <p className="text-lg text-slate-300">
              Tell me about your project — I’ll suggest the best solution for
              your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="pb-28 bg-slate-950">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* LEFT */}
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-2xl font-semibold text-white">
                Contact Information
              </h2>

              <p className="text-slate-400">
                Trusted by 10+ businesses. Available for websites, SaaS, and
                custom systems.
              </p>

              {contactMethods.map((method) => (
                <a
                  key={method.label}
                  href={method.href || "#"}
                  target={method.href ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 p-4 backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl hover:border-emerald-400/40 hover:scale-[1.02] transition"
                >
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center text-slate-300 group-hover:bg-gradient-to-r group-hover:from-emerald-400 group-hover:to-blue-500 group-hover:text-black transition">
                    <method.icon size={20} />
                  </div>

                  <div className="flex-1">
                    <p className="text-sm text-slate-400">{method.label}</p>
                    <p className="text-white font-medium group-hover:text-emerald-400">
                      {method.value}
                    </p>
                  </div>

                  <ArrowUpRight size={18} className="text-slate-400" />
                </a>
              ))}
            </div>

            {/* RIGHT FORM */}
            <div className="lg:col-span-3">
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-white mb-4">
                  Send a Message
                </h3>

                <p className="text-slate-400 mb-6">
                  Describe your requirement and I’ll get back within 24 hours.
                </p>

                {submitted ? (
                  <div className="text-center py-10">
                    <CheckCircle
                      size={40}
                      className="text-emerald-400 mx-auto mb-4"
                    />
                    <h4 className="text-xl text-white mb-2">
                      Message Sent Successfully
                    </h4>
                    <p className="text-slate-400">
                      I’ll get back to you shortly.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label>Name</Label>
                        <Input
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                        />
                      </div>

                      <div>
                        <Label>Email</Label>
                        <Input
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Subject</Label>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <Label>Message</Label>
                      <Textarea
                        name="message"
                        rows={5}
                        required
                        value={formData.message}
                        onChange={handleChange}
                      />
                    </div>

                    <Button
                      disabled={loading}
                      className="w-full py-6 bg-gradient-to-r from-emerald-400 to-blue-500 text-black font-semibold hover:scale-[1.02] transition"
                    >
                      {loading ? "Sending..." : "Send Message"}
                      <Send size={16} className="ml-2" />
                    </Button>

                    <p className="text-center text-xs text-slate-500">
                      Trusted by businesses • Fast response within 24 hours
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
